/**
 * De teksten van de zelfregistratie (Stap 34, REG-2), in vier talen.
 *
 * Een eigen woordenboek en niet de vertaalbestanden van de app: de app spreekt Nederlands, Frans en
 * Engels, en de spec laat een nieuwe medewerker ook Polski kiezen (frame 2). Wie zich registreert,
 * heeft nog geen account en dus nog geen taal; deze flow is de enige plek waar die vierde taal
 * nodig is. Na het registreren wordt Pools de leestaal en is de interface Engels (ADR-0043).
 *
 * De Poolse zinnen zijn te laten nalezen door een moedertaalspreker (spec §3, `demo-schuld.md`).
 *
 * De woorden volgen het type organisatie zodra de uitnodiging bekend is (ORG-3): `joinTextsFor` haalt
 * elke tekst door dezelfde nabewerking als de app (ADR-0087), ook de teksten met een naam of een
 * datum erin. Pools heeft geen tabel en blijft zoals het is.
 */
import { applyTerminology, hasTerminology, type OrgType } from '@up2d8/contracts';

export const JOIN_LANGUAGES = [
  { code: 'nl', label: 'Nederlands' },
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
] as const;
export type JoinLanguage = (typeof JOIN_LANGUAGES)[number]['code'];

export interface JoinTexts {
  codeTitle: string;
  codeIntro: string;
  codeHint: string;
  codeLabel: string;
  scan: string;
  scanHint: string;
  next: string;
  invites: (company: string) => string;
  youJoin: string;
  setByEmployer: string;
  howItWorks: string;
  steps: [string, string, string];
  begin: string;
  stepOf: (step: number) => string;
  detailsTitle: string;
  firstName: string;
  lastName: string;
  required: string;
  optional: string;
  fieldMissing: (label: string) => string;
  employeeNumberMissing: string;
  pinTitle: string;
  pinRepeat: string;
  pinIntro: string;
  pinWeak: string;
  pinMismatch: string;
  doneTitle: (name: string) => string;
  doneIntro: string;
  doneLogin: (code: string, number: string) => string;
  toApp: string;
  errors: Record<
    | 'EXPIRED'
    | 'REVOKED'
    | 'FULL'
    | 'UNKNOWN'
    | 'EMPLOYEE_NUMBER_TAKEN'
    | 'AWAITING_APPROVAL'
    | 'RATE_LIMITED'
    | 'OFFLINE',
    { title: string; text: (detail: string) => string; button: string | null }
  >;
  otherCode: string;
  desktopTitle: string;
  desktopText: string;
  desktopContinue: string;
  haveCode: string;
}

const NL: JoinTexts = {
  codeTitle: 'Vul je uitnodigingscode in',
  codeIntro: 'De code staat op de poster of in het bericht van je werkgever.',
  codeHint: '8 tekens, bijvoorbeeld K7QM-24TX',
  codeLabel: 'Uitnodigingscode',
  scan: 'Scan de QR-code',
  scanHint: 'Open de camera van je telefoon en richt hem op de QR-code van de poster.',
  next: 'Ga verder',
  invites: (company) => `${company} nodigt je uit`,
  youJoin: 'Je komt in',
  setByEmployer: 'Dit is ingesteld door je werkgever. Je kunt het hier niet wijzigen.',
  howItWorks: 'Zo werkt het',
  steps: ['Vul je gegevens in', 'Kies een PIN van 6 cijfers', 'Je bent meteen binnen'],
  begin: 'Begin',
  stepOf: (step) => `Stap ${step} van 3`,
  detailsTitle: 'Je gegevens',
  firstName: 'Voornaam',
  lastName: 'Achternaam',
  required: 'verplicht',
  optional: 'optioneel',
  fieldMissing: (label) => `Vul ${label.toLowerCase()} in.`,
  employeeNumberMissing: 'Vul je personeelsnummer in. Het staat op je badge.',
  pinTitle: 'Kies een PIN van 6 cijfers',
  pinRepeat: 'Typ je PIN nog een keer',
  pinIntro: 'Hiermee meld je je aan. Niemand van je werkgever kent hem.',
  pinWeak:
    'Deze PIN is te makkelijk te raden. Kies geen reeks zoals 123456 of zes dezelfde cijfers.',
  pinMismatch: "De twee PIN's zijn niet gelijk. Probeer het opnieuw.",
  doneTitle: (name) => `Welkom, ${name}`,
  doneIntro: 'Je account is klaar. Je bent aangemeld.',
  doneLogin: (code, number) =>
    `Aanmelden doe je voortaan met bedrijfscode ${code}, personeelsnummer ${number} en je PIN.`,
  toApp: 'Naar de app',
  errors: {
    EXPIRED: {
      title: 'Deze uitnodiging is verlopen',
      text: (date) => `Ze was geldig tot ${date}. Vraag je ploegleider of HR om een nieuwe.`,
      button: 'Vul een andere code in',
    },
    REVOKED: {
      title: 'Deze uitnodiging werkt niet meer',
      text: () => 'Vraag HR om een nieuwe.',
      button: 'Vul een andere code in',
    },
    FULL: {
      title: 'Deze uitnodiging is volzet',
      text: () => 'Ze is al door het maximale aantal mensen gebruikt. Vraag HR om een nieuwe.',
      button: 'Vul een andere code in',
    },
    UNKNOWN: {
      title: 'Deze code klopt niet',
      text: () => 'Kijk hem na of scan de QR-code opnieuw.',
      button: 'Probeer opnieuw',
    },
    EMPLOYEE_NUMBER_TAKEN: {
      title: 'Er bestaat al een account met dit personeelsnummer',
      text: () => 'Neem contact op met HR.',
      button: 'Terug naar mijn gegevens',
    },
    AWAITING_APPROVAL: {
      title: 'Je aanmelding wacht op goedkeuring',
      text: () =>
        'Dit personeelsnummer staat al in de lijst van je werkgever. HR bevestigt eerst dat jij het bent; daarna meld je je aan met je personeelsnummer en de pincode die je net koos.',
      button: null,
    },
    RATE_LIMITED: {
      title: 'Te veel pogingen',
      text: (minutes) => `Probeer het over ${minutes} minuten opnieuw.`,
      button: null,
    },
    OFFLINE: {
      title: 'Geen verbinding',
      text: () => 'Registreren kan alleen met verbinding. Wat je al invulde, blijft staan.',
      button: 'Probeer opnieuw',
    },
  },
  otherCode: 'Vul een andere code in',
  desktopTitle: 'Open deze uitnodiging op je telefoon',
  desktopText:
    'Scan de QR-code met de camera van je telefoon. Zo staat de app meteen waar je hem nodig hebt.',
  desktopContinue: 'Ga verder op deze computer',
  haveCode: 'Ik heb een uitnodigingscode',
};

const FR: JoinTexts = {
  codeTitle: "Saisissez votre code d'invitation",
  codeIntro: "Le code figure sur l'affiche ou dans le message de votre employeur.",
  codeHint: '8 caractères, par exemple K7QM-24TX',
  codeLabel: "Code d'invitation",
  scan: 'Scanner le code QR',
  scanHint: "Ouvrez l'appareil photo de votre téléphone et visez le code QR de l'affiche.",
  next: 'Continuer',
  invites: (company) => `${company} vous invite`,
  youJoin: 'Vous rejoignez',
  setByEmployer: 'Défini par votre employeur. Vous ne pouvez pas le modifier ici.',
  howItWorks: 'Comment ça marche',
  steps: ['Remplissez vos données', 'Choisissez un code PIN à 6 chiffres', 'Vous êtes connecté'],
  begin: 'Commencer',
  stepOf: (step) => `Étape ${step} sur 3`,
  detailsTitle: 'Vos données',
  firstName: 'Prénom',
  lastName: 'Nom',
  required: 'obligatoire',
  optional: 'facultatif',
  fieldMissing: (label) => `Indiquez votre ${label.toLowerCase()}.`,
  employeeNumberMissing: 'Indiquez votre numéro de personnel. Il figure sur votre badge.',
  pinTitle: 'Choisissez un code PIN à 6 chiffres',
  pinRepeat: 'Saisissez à nouveau votre code PIN',
  pinIntro: 'Il vous sert à vous connecter. Personne chez votre employeur ne le connaît.',
  pinWeak:
    'Ce code PIN est trop facile à deviner. Évitez une suite comme 123456 ou six chiffres identiques.',
  pinMismatch: 'Les deux codes PIN ne correspondent pas. Réessayez.',
  doneTitle: (name) => `Bienvenue, ${name}`,
  doneIntro: 'Votre compte est prêt. Vous êtes connecté.',
  doneLogin: (code, number) =>
    `Pour vous connecter : code entreprise ${code}, numéro de personnel ${number} et votre code PIN.`,
  toApp: "Vers l'application",
  errors: {
    EXPIRED: {
      title: 'Cette invitation a expiré',
      text: (date) =>
        `Elle était valable jusqu'au ${date}. Demandez-en une nouvelle à votre chef d'équipe ou aux RH.`,
      button: 'Saisir un autre code',
    },
    REVOKED: {
      title: 'Cette invitation ne fonctionne plus',
      text: () => 'Demandez-en une nouvelle aux RH.',
      button: 'Saisir un autre code',
    },
    FULL: {
      title: 'Cette invitation est complète',
      text: () => 'Le nombre maximal de personnes a été atteint. Demandez-en une nouvelle aux RH.',
      button: 'Saisir un autre code',
    },
    UNKNOWN: {
      title: "Ce code n'est pas correct",
      text: () => 'Vérifiez-le ou scannez à nouveau le code QR.',
      button: 'Réessayer',
    },
    EMPLOYEE_NUMBER_TAKEN: {
      title: 'Un compte existe déjà avec ce numéro de personnel',
      text: () => 'Contactez les RH.',
      button: 'Retour à mes données',
    },
    AWAITING_APPROVAL: {
      title: 'Ton inscription attend une validation',
      text: () =>
        'Ce numéro de personnel figure déjà dans la liste de ton employeur. Les RH confirment d’abord que c’est bien toi ; ensuite tu te connectes avec ton numéro de personnel et le code PIN que tu viens de choisir.',
      button: null,
    },
    RATE_LIMITED: {
      title: 'Trop de tentatives',
      text: (minutes) => `Réessayez dans ${minutes} minutes.`,
      button: null,
    },
    OFFLINE: {
      title: 'Pas de connexion',
      text: () => "L'inscription nécessite une connexion. Ce que vous avez saisi est conservé.",
      button: 'Réessayer',
    },
  },
  otherCode: 'Saisir un autre code',
  desktopTitle: 'Ouvrez cette invitation sur votre téléphone',
  desktopText: "Scannez le code QR avec l'appareil photo de votre téléphone.",
  desktopContinue: 'Continuer sur cet ordinateur',
  haveCode: "J'ai un code d'invitation",
};

const EN: JoinTexts = {
  codeTitle: 'Enter your invitation code',
  codeIntro: "You'll find the code on the poster or in your employer's message.",
  codeHint: '8 characters, for example K7QM-24TX',
  codeLabel: 'Invitation code',
  scan: 'Scan the QR code',
  scanHint: "Open your phone's camera and point it at the QR code on the poster.",
  next: 'Continue',
  invites: (company) => `${company} invites you`,
  youJoin: 'You join',
  setByEmployer: 'Set by your employer. You cannot change it here.',
  howItWorks: 'How it works',
  steps: ['Fill in your details', 'Choose a 6-digit PIN', "You're in straight away"],
  begin: 'Start',
  stepOf: (step) => `Step ${step} of 3`,
  detailsTitle: 'Your details',
  firstName: 'First name',
  lastName: 'Last name',
  required: 'required',
  optional: 'optional',
  fieldMissing: (label) => `Fill in your ${label.toLowerCase()}.`,
  employeeNumberMissing: "Fill in your employee number. It's on your badge.",
  pinTitle: 'Choose a 6-digit PIN',
  pinRepeat: 'Type your PIN again',
  pinIntro: 'You use it to sign in. Nobody at your employer knows it.',
  pinWeak:
    "This PIN is too easy to guess. Don't use a sequence like 123456 or six identical digits.",
  pinMismatch: "The two PINs don't match. Try again.",
  doneTitle: (name) => `Welcome, ${name}`,
  doneIntro: 'Your account is ready. You are signed in.',
  doneLogin: (code, number) =>
    `To sign in later: company code ${code}, employee number ${number} and your PIN.`,
  toApp: 'Go to the app',
  errors: {
    EXPIRED: {
      title: 'This invitation has expired',
      text: (date) => `It was valid until ${date}. Ask your team lead or HR for a new one.`,
      button: 'Enter another code',
    },
    REVOKED: {
      title: 'This invitation no longer works',
      text: () => 'Ask HR for a new one.',
      button: 'Enter another code',
    },
    FULL: {
      title: 'This invitation is full',
      text: () => 'It has been used by the maximum number of people. Ask HR for a new one.',
      button: 'Enter another code',
    },
    UNKNOWN: {
      title: 'This code is not right',
      text: () => 'Check it or scan the QR code again.',
      button: 'Try again',
    },
    EMPLOYEE_NUMBER_TAKEN: {
      title: 'An account with this employee number already exists',
      text: () => 'Please contact HR.',
      button: 'Back to my details',
    },
    AWAITING_APPROVAL: {
      title: 'Your registration is awaiting approval',
      text: () =>
        'This employee number is already on your employer’s list. HR first confirms that it is you; then you sign in with your employee number and the PIN you just chose.',
      button: null,
    },
    RATE_LIMITED: {
      title: 'Too many attempts',
      text: (minutes) => `Try again in ${minutes} minutes.`,
      button: null,
    },
    OFFLINE: {
      title: 'No connection',
      text: () => 'You can only register with a connection. What you filled in stays here.',
      button: 'Try again',
    },
  },
  otherCode: 'Enter another code',
  desktopTitle: 'Open this invitation on your phone',
  desktopText: "Scan the QR code with your phone's camera.",
  desktopContinue: 'Continue on this computer',
  haveCode: 'I have an invitation code',
};

const PL: JoinTexts = {
  codeTitle: 'Wpisz swój kod zaproszenia',
  codeIntro: 'Kod znajdziesz na plakacie lub w wiadomości od pracodawcy.',
  codeHint: '8 znaków, na przykład K7QM-24TX',
  codeLabel: 'Kod zaproszenia',
  scan: 'Zeskanuj kod QR',
  scanHint: 'Otwórz aparat w telefonie i skieruj go na kod QR na plakacie.',
  next: 'Dalej',
  invites: (company) => `${company} zaprasza Cię`,
  youJoin: 'Dołączasz do',
  setByEmployer: 'Ustawione przez pracodawcę. Nie możesz tego tutaj zmienić.',
  howItWorks: 'Jak to działa',
  steps: ['Uzupełnij swoje dane', 'Wybierz 6-cyfrowy PIN', 'Od razu jesteś w aplikacji'],
  begin: 'Zacznij',
  stepOf: (step) => `Krok ${step} z 3`,
  detailsTitle: 'Twoje dane',
  firstName: 'Imię',
  lastName: 'Nazwisko',
  required: 'wymagane',
  optional: 'opcjonalne',
  fieldMissing: (label) => `Uzupełnij pole: ${label.toLowerCase()}.`,
  employeeNumberMissing: 'Wpisz swój numer pracownika. Znajdziesz go na identyfikatorze.',
  pinTitle: 'Wybierz 6-cyfrowy PIN',
  pinRepeat: 'Wpisz PIN jeszcze raz',
  pinIntro: 'Służy do logowania. Nikt u pracodawcy go nie zna.',
  pinWeak:
    'Ten PIN jest zbyt łatwy do odgadnięcia. Nie wybieraj ciągu jak 123456 ani sześciu takich samych cyfr.',
  pinMismatch: 'Oba numery PIN nie są takie same. Spróbuj ponownie.',
  doneTitle: (name) => `Witaj, ${name}`,
  doneIntro: 'Twoje konto jest gotowe. Jesteś zalogowany.',
  doneLogin: (code, number) =>
    `Aby się zalogować: kod firmy ${code}, numer pracownika ${number} i Twój PIN.`,
  toApp: 'Przejdź do aplikacji',
  errors: {
    EXPIRED: {
      title: 'To zaproszenie wygasło',
      text: (date) => `Było ważne do ${date}. Poproś brygadzistę lub dział kadr o nowe.`,
      button: 'Wpisz inny kod',
    },
    REVOKED: {
      title: 'To zaproszenie już nie działa',
      text: () => 'Poproś dział kadr o nowe.',
      button: 'Wpisz inny kod',
    },
    FULL: {
      title: 'To zaproszenie jest już wykorzystane',
      text: () => 'Skorzystała z niego maksymalna liczba osób. Poproś dział kadr o nowe.',
      button: 'Wpisz inny kod',
    },
    UNKNOWN: {
      title: 'Ten kod jest nieprawidłowy',
      text: () => 'Sprawdź go lub zeskanuj kod QR ponownie.',
      button: 'Spróbuj ponownie',
    },
    EMPLOYEE_NUMBER_TAKEN: {
      title: 'Istnieje już konto z tym numerem pracownika',
      text: () => 'Skontaktuj się z działem kadr.',
      button: 'Wróć do moich danych',
    },
    AWAITING_APPROVAL: {
      title: 'Twoja rejestracja czeka na zatwierdzenie',
      text: () =>
        'Ten numer pracownika jest już na liście Twojego pracodawcy. Dział kadr najpierw potwierdzi, że to Ty; potem zalogujesz się numerem pracownika i wybranym przed chwilą PIN-em.',
      button: null,
    },
    RATE_LIMITED: {
      title: 'Zbyt wiele prób',
      text: (minutes) => `Spróbuj ponownie za ${minutes} min.`,
      button: null,
    },
    OFFLINE: {
      title: 'Brak połączenia',
      text: () => 'Rejestracja wymaga połączenia. To, co już wpisałeś, zostaje zachowane.',
      button: 'Spróbuj ponownie',
    },
  },
  otherCode: 'Wpisz inny kod',
  desktopTitle: 'Otwórz to zaproszenie w telefonie',
  desktopText: 'Zeskanuj kod QR aparatem w telefonie.',
  desktopContinue: 'Kontynuuj na tym komputerze',
  haveCode: 'Mam kod zaproszenia',
};

export const JOIN_TEXTS: Record<JoinLanguage, JoinTexts> = { nl: NL, fr: FR, en: EN, pl: PL };

export function isJoinLanguage(value: string | null | undefined): value is JoinLanguage {
  return JOIN_LANGUAGES.some((language) => language.code === value);
}

/**
 * De teksten in een taal, met de woorden van het type organisatie (ORG-3).
 *
 * Een tekst met een naam of een datum erin is een functie. Alleen het vaste deel wordt omgezet: een
 * clubnaam als "Bedrijfsbond" blijft zoals hij is (ADR-0087: wat iemand typte, wordt niet omgezet).
 * Zie `fixTemplate`.
 */
export function joinTextsFor(
  language: JoinLanguage,
  orgType: OrgType | null | undefined,
): JoinTexts {
  const texts = JOIN_TEXTS[language];
  if (!orgType || !hasTerminology(orgType)) return texts;
  return convert(texts, (text) => applyTerminology(text, orgType, language)) as JoinTexts;
}

const mark = (index: number) => `\uE000${index}\uE000`;
const MARK = /\uE000(\d+)\uE000/g;
const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Zet het vaste deel van een tekst-met-waarden om, en laat de waarden zoals de tekst ze toont.
 *
 * De functie wordt twee keer aangeroepen: met merktekens (het sjabloon) en met de echte waarden. De
 * stukken tussen de merktekens zijn in beide gelijk; daarmee vindt dit in de echte tekst wat er op
 * de plaats van elke waarde staat - ook als de tekst er iets mee deed, zoals `label.toLowerCase()`.
 * Daarna gaat alleen het sjabloon door de nabewerking, en komen de waarden terug op hun plaats.
 */
export function fixTemplate(
  render: (...args: never[]) => unknown,
  args: unknown[],
  fix: (text: string) => string,
): string {
  const real = String((render as (...a: unknown[]) => unknown)(...args));
  const template = String(
    (render as (...a: unknown[]) => unknown)(...args.map((_, index) => mark(index))),
  );
  const order = [...template.matchAll(MARK)].map((match) => Number(match[1]));
  if (order.length === 0) return fix(real);
  const pieces = template.split(MARK).filter((_, index) => index % 2 === 0);
  const found = new RegExp(`^${pieces.map(escape).join('([\\s\\S]*?)')}$`).exec(real);
  // Past het niet (een functie die meer doet dan invullen), dan de tekst zoals ze was.
  if (!found) return real;
  const shown = new Map(order.map((index, position) => [index, found[position + 1] ?? '']));
  return fix(template).replace(MARK, (_, index: string) => shown.get(Number(index)) ?? '');
}

function convert(value: unknown, fix: (text: string) => string): unknown {
  if (typeof value === 'string') return fix(value);
  if (typeof value === 'function') {
    const render = value as (...args: never[]) => unknown;
    return (...args: unknown[]) => fixTemplate(render, args, fix);
  }
  if (Array.isArray(value)) return value.map((item) => convert(item, fix));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, convert(item, fix)]),
    );
  }
  return value;
}
